import React, { useEffect, useState } from 'react'
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Avatar,
  Tab,
  Button,
  Paper,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  Badge,
  Tabs,
  Drawer,
  ListItemAvatar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon, 
  Refresh as RefreshIcon, 
  PlayArrow as PlayArrowIcon, 
  Pause as PauseIcon, 
  StarBorder as StarBorderIcon, 
  ShowChart as ShowChartIcon, 
  CandlestickChart as CandlestickChartIcon, 
  Timeline as TimelineIcon, 
  AutoGraph as AutoGraphIcon, 
  BarChart as BarChartIcon,
  MoreVert as MoreVertIcon,
  Fullscreen as FullscreenIcon,
  SmartToy as SmartToyIcon,
  ContentCopy as ContentCopyIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';

// Import existing components
import MarketTabs from '../Markets/MarketTabs';
import ListStock from '../Stock/ListStock';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import CoinSlide from '../BybitTrade/CoinSlide';
import BalanceCard from '../Cards/BalanceCard';
import MobileMenu from './MobileMenu';
import StockSlide from '../Stock/StockSlide';
import SmallChart from '../Traders/SmallChart';

// Redux
import { setcurrentPair, setPairType } from '../Trader/tradingSlice';
import { useNavigate } from 'react-router-dom';

// Professional Trading Dashboard Styled Components
const TradingDashboard = styled(Box)(({ theme }) => ({
  padding: '0 8px 0 8px',
  background: '#0B0E11',
  minHeight: '100vh',
  color: '#FFFFFF',
  fontFamily: 'Inter, sans-serif',
  [theme.breakpoints.up('md')]: {
    padding: '0 12px 0 12px',
  }
}));

const TradingHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
  padding: '8px 12px',
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '6px',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: '6px 8px',
    marginBottom: '6px'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, #F7931A 0%, #FFD700 50%, #F7931A 100%)',
  }
}));

// Copy Trader Slider Components
const CopyTraderCard = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '4px',
  padding: '8px',
  minWidth: '180px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    minWidth: '150px',
    padding: '6px'
  },
  '&:hover': {
    borderColor: '#F7931A',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(247, 147, 26, 0.2)'
  }
}));

const CopySlider = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  height: '130px',
  overflowY: 'hidden',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    height: '110px',
    gap: '5px'
  },
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    height: '8px',
    zIndex: 2,
    pointerEvents: 'none'
  },
  '&::before': {
    top: 0,
    background: 'linear-gradient(to bottom, #161A1E, transparent)'
  },
  '&::after': {
    bottom: 0,
    background: 'linear-gradient(to top, #161A1E, transparent)'
  }
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  animation: 'verticalScroll 60s linear infinite',
  [theme.breakpoints.down('md')]: {
    gap: '5px',
    animation: 'verticalScroll 50s linear infinite'
  },
  '&:hover': {
    animationPlayState: 'paused'
  },
  '@keyframes verticalScroll': {
    '0%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-100%)' },
    '100%': { transform: 'translateY(0)' }
  }
}));

// Auto-scrolling Asset Ticker Container (allows manual scroll)
const AssetTickerContainer = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  '&:hover .auto-scroll-content': {
    animationPlayState: 'paused'
  }
}));

// Auto-scrolling Asset Ticker Content
const AutoScrollAssetTicker = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  animation: 'horizontalScroll 16s linear infinite',
  width: 'fit-content',
  '@keyframes horizontalScroll': {
    '0%': { transform: 'translateX(0)' },
    '100%': { transform: 'translateX(-50%)' }
  }
}));

const PriceTickerCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#F7931A',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(247, 147, 26, 0.2)'
  }
}));

const TradingCard = styled(Card)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  height: '100%',
  '& .MuiCardContent-root': {
    padding: '16px',
    '&:last-child': {
      paddingBottom: '16px'
    }
  }
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  height: '400px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column'
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  borderBottom: '1px solid #2B3139',
  background: '#1E2328'
}));

const OrderBookContainer = styled(Box)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  height: '400px',
  overflow: 'hidden'
}));

const OrderBookHeader = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: '1px solid #2B3139',
  background: '#1E2328',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const QuickTradePanel = styled(Box)(({ theme }) => ({
  background: '#161A1E',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  padding: '16px'
}));

const TradeButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #02C076 0%, #05E085 100%)',
  color: '#FFFFFF',
  fontWeight: 600,
  padding: '12px 24px',
  borderRadius: '6px',
  textTransform: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #05E085 0%, #02C076 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(2, 192, 118, 0.4)'
  },
  '&.sell': {
    background: 'linear-gradient(135deg, #F84960 0%, #FF6B85 100%)',
    '&:hover': {
      background: 'linear-gradient(135deg, #FF6B85 0%, #F84960 100%)',
      boxShadow: '0 4px 12px rgba(248, 73, 96, 0.4)'
    }
  }
}));

const PortfolioMetric = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '16px',
  background: '#1E2328',
  borderRadius: '6px',
  border: '1px solid #2B3139',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#F7931A',
    background: '#2B3139'
  }
}));

const LiveChart = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(2, 192, 118, 0.05)',
  position: 'relative',
  '&::after': {
    content: '"⚡"',
    position: 'absolute',
    fontSize: '48px',
    opacity: 0.1
  }
}));

const AssetTabs = styled(Tabs)(({ theme }) => ({
  background: '#1E2328',
  borderRadius: '8px',
  minHeight: '40px',
  '& .MuiTabs-indicator': {
    backgroundColor: '#F7931A',
    height: '3px',
    borderRadius: '2px'
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    minHeight: '40px',
    color: '#B7BDC6',
    '&.Mui-selected': {
      color: '#F7931A'
    }
  }
}));

const AssetTickerCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
  border: '1px solid #2B3139',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#F7931A',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 20px rgba(247, 147, 26, 0.15)'
  }
}));

const DepositButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #F7931A 0%, #FFD700 100%)',
  color: '#000',
  fontWeight: 700,
  textTransform: 'none',
  borderRadius: '8px',
  padding: '8px 16px',
  minWidth: 'auto',
  '&:hover': {
    background: 'linear-gradient(135deg, #FFD700 0%, #F7931A 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3)'
  },
  [theme.breakpoints.down('md')]: {
    padding: '6px 12px',
    fontSize: '12px'
  }
}));

const Index = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user.user);
  const coinRates = useSelector((state) => state.coins.coinRates);
  const stock = useSelector((state) => state.coins.stock);
  const fundings = useSelector((state) => state.wallet.fundings);
  const tradings = useSelector((state) => state.history.tradings);
  const investments = useSelector((state) => state.history.investments);
  const withdrawals = useSelector((state) => state.history.withdrawals);
  const traders = useSelector((state) => state.traders?.traders) || [];
  const fundingBal = useSelector((state) => state.wallet.fundingBalance); // Real total assets
  const plans = useSelector((state) => state.plans.plans);
  const categories = useSelector((state) => state.plans.categories);
  
  const [selectedPair, setSelectedPair] = useState('BTC/USDT');
  const [orderBookData, setOrderBookData] = useState({ bids: [], asks: [] });
  const [activeTab, setActiveTab] = useState('0');
  const [assetType, setAssetType] = useState('crypto'); // Changed default to 'crypto'
  const [depositDrawerOpen, setDepositDrawerOpen] = useState(false);

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

  // Deposit drawer handlers
  const handleDepositClick = () => {
    setDepositDrawerOpen(true);
  };

  const handleDepositDrawerClose = () => {
    setDepositDrawerOpen(false);
  };

  // Remove mock real-time data updates - keeping only orderbook simulation for trading functionality
  useEffect(() => {
    const interval = setInterval(() => {
      // Only simulate orderbook updates for trading functionality
      const newBids = Array.from({ length: 10 }, (_, i) => ({
        price: (50000 - i * 10 + Math.random() * 5).toFixed(2),
        amount: (Math.random() * 10).toFixed(4),
        total: (Math.random() * 500000).toFixed(2)
      }));
      const newAsks = Array.from({ length: 10 }, (_, i) => ({
        price: (50020 + i * 10 + Math.random() * 5).toFixed(2),
        amount: (Math.random() * 10).toFixed(4),
        total: (Math.random() * 500000).toFixed(2)
      }));
      setOrderBookData({ bids: newBids, asks: newAsks });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const topPairs = [
    { symbol: 'BTC/USDT', price: '97,234.50', change: '+2.45%', volume: '1.2B', isPositive: true },
    { symbol: 'ETH/USDT', price: '3,456.78', change: '+1.23%', volume: '890M', isPositive: true },
    { symbol: 'BNB/USDT', price: '645.32', change: '-0.87%', volume: '456M', isPositive: false },
    { symbol: 'SOL/USDT', price: '234.56', change: '+5.67%', volume: '234M', isPositive: true },
    { symbol: 'ADA/USDT', price: '1.2345', change: '+3.21%', volume: '123M', isPositive: true },
    { symbol: 'DOT/USDT', price: '12.34', change: '-1.45%', volume: '67M', isPositive: false }
  ];

  const recentTrades = [
    { time: '15:23:45', pair: 'BTC/USDT', side: 'BUY', price: '97,234.50', amount: '0.0123' },
    { time: '15:23:44', pair: 'BTC/USDT', side: 'SELL', price: '97,230.25', amount: '0.0456' },
    { time: '15:23:43', pair: 'BTC/USDT', side: 'BUY', price: '97,235.75', amount: '0.0789' },
    { time: '15:23:42', pair: 'ETH/USDT', side: 'BUY', price: '3,456.78', amount: '1.234' },
    { time: '15:23:41', pair: 'ETH/USDT', side: 'SELL', price: '3,454.32', amount: '2.567' }
  ];

  // Get copy traders data - use only real traders from Redux
  const getCopyTraders = () => {
    return traders.map(trader => ({
      id: trader.id,
      name: trader.trader_name || 'Anonymous',
      level: trader.level || 'Verified',
      roi: `${trader.gain || 0}%`,
      followers: trader.copiers || 0,
      duration: trader.duration || '30 Days',
      image: trader.image || `https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face`,
      verified: true
    }));
  };

  const copyTraders = getCopyTraders();

  const formatFollowers = (count) => {
    count = parseInt(count);
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count;
  };

  // Calculate balance for crypto
  const calculateBalance = (coin) => {
    let amount = 0;
    fundings.forEach(fund => {
      if (fund.gateway === coin) {
        amount += parseFloat(fund.amount);
      }
    });
    tradings.forEach(trade => {
      if (trade.pair === coin) {
        amount += parseFloat(trade.profit);
      }
    });
    investments.forEach(investment => {
      if (investment.payment_source === coin && investment.status !== 'declined') {
        amount -= parseFloat(investment.amount_invested);
      }
    });
    withdrawals.forEach(withdrawal => {
      if (withdrawal.source === coin && withdrawal.status !== 'declined') {
        amount -= parseFloat(withdrawal.amount);
      }
    });
    return amount;
  };

  // Calculate 24hr change for crypto
  const calculate24hrChange = (coin) => {
    let rate = 0;
    coinRates.forEach(coinRater => {
      if (coinRater.symbol === coin) {
        rate = parseFloat(coinRater.changePercent24Hr);
      }
    });
    return rate;
  };

  // Handle asset click
  const handleAssetClick = (asset, type) => {
    if (type === 'crypto') {
      dispatch(setPairType('crypto'));
      if (asset.symbol === 'USDT') {
        dispatch(setcurrentPair('BTC'));
      } else {
        dispatch(setcurrentPair(asset.symbol));
      }
    } else {
      dispatch(setPairType('stocks'));
      dispatch(setcurrentPair(asset.short_name));
    }
    
    setTimeout(() => {
      navigate('/trading-page');
    }, 100);
  };

  // Handle copy trader click
  const handleCopyTraderClick = (traderId) => {
    if (typeof traderId === 'string' && traderId.startsWith('mock')) {
      // For mock traders, redirect to copy-plans page
      navigate('/copy-plans');
    } else {
      // For real traders, redirect to their specific page
      navigate(`/view-trades/${traderId}`);
    }
  };

  // Get top assets to display
  const getTopAssets = () => {
    if (assetType === 'crypto') {
      return coinRates.slice(0, 10) || [];
    } else {
      return stock.slice(0, 10) || [];
    }
  };

  // Get portfolio assets
  const getPortfolioAssets = () => {
    const assets = [];
    
    if (assetType === 'crypto') {
      coinRates.slice(0, 6).forEach((coin) => {
        const balance = calculateBalance(coin.symbol);
        const change = calculate24hrChange(coin.symbol);
        assets.push({
          symbol: coin.symbol,
          name: coin.name,
          priceUsd: coin.priceUsd,
          amount: `${(balance/parseFloat(coin.priceUsd)).toFixed(6)} ${coin.symbol}`,
          usdValue: formatPrice(balance).replace('$', ''),
          change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
          logo: `https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`,
          balance,
          volumeUsd24Hr: coin.volumeUsd24Hr
        });
      });
    } else {
      stock.slice(0, 6).forEach((stockItem) => {
        const change = parseFloat(stockItem.change_percentage || 0);
        assets.push({
          symbol: stockItem.short_name,
          name: stockItem.name,
          priceUsd: stockItem.priceUsd,
          amount: `0 shares`, // No user stock balance available
          usdValue: formatPrice(0),
          change: `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`,
          logo: `https://img.logo.dev/ticker/${stockItem.short_name}?token=pk_Pb2cclXnTuuh7Lsj1Ys7ew&retina=true`,
          balance: 0
        });
      });
    }
    
    return assets;
  };

  const topAssets = getTopAssets();
  const portfolioAssets = getPortfolioAssets();

  return (
    <TradingDashboard>
      {/* Balance & Copy Trade Header */}
      <TradingHeader>
        <Grid container spacing={1} alignItems="center">
          {/* Left Side - Total Assets */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#FFFFFF', 
                    fontSize: { xs: '15px', md: '17px' }, 
                    mb: { xs: 0.5, md: 0.75 },
                    textAlign: 'left'
                  }}
                >
                  Total Assets
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#FFFFFF', 
                    fontSize: { xs: '21px', md: '25px' },
                    textAlign: 'left',
                    lineHeight: 1.1
                  }}
                >
                  {formatPrice(fundingBal)}
                </Typography>
              </Box>
              <DepositButton
                onClick={handleDepositClick}
                startIcon={
                  <Avatar 
                    src="https://res.cloudinary.com/dqi4rtyvh/image/upload/v1738590761/deposit_bvwyrg.png"
                    sx={{ 
                      width: { xs: 21, md: 23 }, 
                      height: { xs: 21, md: 23 },
                      marginLeft: '-4px',
                      marginRight: '4px'
                    }}
                  />
                }
                sx={{
                  fontSize: { xs: '15px', md: '17px' },
                  height: { xs: '33px', md: '37px' },
                  padding: { xs: '6px 12px', md: '8px 16px' }
                }}
              >
                Deposit
              </DepositButton>
            </Box>
          </Grid>

          {/* Right Side - Quick Copy Trade */}
          <Grid item xs={12} md={6}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: { xs: '11px', md: '13px' } }}>
                  Quick Copy Trade
                </Typography>
                <Button
                  component={RouterLink}
                  to="/copy-plans"
                  size="small"
                  sx={{
                    background: 'rgba(247, 147, 26, 0.1)',
                    color: '#F7931A',
                    border: '1px solid rgba(247, 147, 26, 0.3)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '8px', md: '9px' },
                    padding: { xs: '2px 4px', md: '3px 5px' },
                    minWidth: { xs: '40px', md: '50px' },
                    height: { xs: '16px', md: '18px' },
                    '&:hover': {
                      background: 'rgba(247, 147, 26, 0.2)',
                      borderColor: '#F7931A'
                    }
                  }}
                >
                  View All
                </Button>
              </Box>
              <CopySlider>
                <SliderContainer>
                  {copyTraders.map((trader, index) => (
                    <CopyTraderCard 
                      key={`${trader.id}-${index}`}
                      onClick={() => handleCopyTraderClick(trader.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 0.75 }, mb: 0.1 }}>
                        <Avatar 
                          src={trader.image}
                          sx={{ 
                            width: { xs: 18, md: 22 }, 
                            height: { xs: 18, md: 22 }, 
                            border: '1px solid #F7931A',
                            fontSize: { xs: '8px', md: '10px' }
                          }}
                        >
                          {trader.name[0]}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 700, 
                                color: '#FFFFFF', 
                                fontSize: { xs: '10px', md: '11px' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {trader.name}
                            </Typography>
                            {trader.verified && (
                              <VerifiedUserIcon sx={{ fontSize: { xs: '6px', md: '8px' }, color: '#02C076' }} />
                            )}
                          </Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#B7BDC6', 
                              fontSize: { xs: '8px', md: '9px' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {trader.level} • {trader.duration}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon sx={{ fontSize: '6px !important' }} />}
                          sx={{
                            background: 'linear-gradient(135deg, #F7931A 0%, #FFD700 100%)',
                            color: '#000',
                            fontWeight: 700,
                            fontSize: { xs: '8px', md: '9px' },
                            minWidth: { xs: '30px', md: '35px' },
                            height: { xs: '14px', md: '16px' },
                            textTransform: 'none',
                            padding: { xs: '1px 2px', md: '1px 3px' },
                            '&:hover': {
                              background: 'linear-gradient(135deg, #FFD700 0%, #F7931A 100%)',
                              transform: 'scale(1.05)'
                            },
                            '& .MuiButton-startIcon': {
                              margin: 0,
                              marginRight: { xs: '1px', md: '1px' }
                            }
                          }}
                        >
                          Copy
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: { xs: '7px', md: '8px' } }}>
                            ROI
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#02C076', fontWeight: 700, fontSize: { xs: '9px', md: '10px' } }}>
                            {trader.roi}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: { xs: '7px', md: '8px' } }}>
                            Followers
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: { xs: '9px', md: '10px' } }}>
                            {formatFollowers(trader.followers)}
                          </Typography>
                        </Box>
                      </Box>
                    </CopyTraderCard>
                  ))}
                </SliderContainer>
              </CopySlider>
            </Box>
          </Grid>
        </Grid>
      </TradingHeader>

      {/* Deposit Options Drawer */}
      <Drawer
        anchor={'bottom'}
        open={depositDrawerOpen}
        onClose={handleDepositDrawerClose}
        sx={{ zIndex: 60000 }}
      >
        <Box sx={{ padding: '10px', marginBottom: '20px' }}>
          <List sx={{ width: '100%', maxWidth: 360 }}>
            <ListItem 
              component={RouterLink} 
              to="/p2p" 
              sx={{ 
                marginY: '2px', 
                borderRadius: '5px', 
                bgcolor: 'background.paper',
                textDecoration: 'none'
              }}
              onClick={handleDepositDrawerClose}
            >
              <ListItemAvatar>
                <Avatar src='https://res.cloudinary.com/dqi4rtyvh/image/upload/v1738672662/peer-to-peer_1_nd9prb.png'>
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={
                  <Typography sx={{ color: 'text.primary' }}>
                    P2P Trading
                  </Typography>
                } 
                secondary="Pay with bank transfer, check and more" 
              />
            </ListItem>

            <Divider variant="inset" component="li" />
            
            <ListItem 
              component={RouterLink} 
              to="/receive" 
              sx={{ 
                marginY: '2px', 
                borderRadius: '5px', 
                bgcolor: 'background.paper',
                textDecoration: 'none'
              }}
              onClick={handleDepositDrawerClose}
            >
              <ListItemAvatar>
                <Avatar src='https://res.cloudinary.com/dqi4rtyvh/image/upload/v1738672665/cryptocurrency_i9vuqz.png'>
                </Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={
                  <Typography sx={{ color: 'text.primary' }}>
                    Deposit Crypto
                  </Typography>
                }
                secondary="Already have crypto ? Deposit directly" 
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Mobile Menu - Moved here after first section */}
      <Box sx={{ mb: 2 }}>
        <MobileMenu />
      </Box>

      {/* Asset Type Tabs */}
      <Box sx={{ mb: 3 }}>
        <AssetTabs
          value={assetType}
          onChange={(e, newValue) => setAssetType(newValue)}
          indicatorColor="primary"
        >
          <Tab label="Stocks" value="stocks" />
          <Tab label="Cryptocurrency" value="crypto" />
        </AssetTabs>
      </Box>

      {/* Asset Ticker - Auto Scrolling with Manual Scroll Support */}
      <Box sx={{ mb: 3, overflow: 'hidden', position: 'relative' }}>
        {/* Gradient overlays for smooth fade effect */}
        <Box sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '40px',
          background: 'linear-gradient(to right, #0B0E11, transparent)',
          zIndex: 2,
          pointerEvents: 'none'
        }} />
        <Box sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '40px',
          background: 'linear-gradient(to left, #0B0E11, transparent)',
          zIndex: 2,
          pointerEvents: 'none'
        }} />
        <AssetTickerContainer>
        <AutoScrollAssetTicker className="auto-scroll-content">
          {/* Duplicate the assets for seamless infinite scroll */}
          {[...topAssets, ...topAssets].map((asset, index) => (
            <AssetTickerCard 
              key={`${asset.symbol || asset.short_name}-${index}`} 
              sx={{ minWidth: '180px', flexShrink: 0 }}
              onClick={() => handleAssetClick(asset, assetType)}
            >
              <CardContent sx={{ padding: '10px !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar 
                      src={assetType === 'crypto' 
                        ? `https://assets.coincap.io/assets/icons/${asset.symbol?.toLowerCase()}@2x.png`
                        : `https://img.logo.dev/ticker/${asset.short_name}?token=pk_Pb2cclXnTuuh7Lsj1Ys7ew&retina=true`
                      }
                      sx={{ width: 20, height: 20 }}
                    >
                      {(asset.symbol || asset.short_name)?.[0]}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF', fontSize: '13px' }}>
                      {asset.symbol || asset.short_name}
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ color: '#B7BDC6' }}>
                    <StarBorderIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 0.5, fontSize: '15px' }}>
                  {formatPrice(asset.priceUsd || Math.random() * 1000)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Chip
                    label={assetType === 'crypto' 
                      ? `${calculate24hrChange(asset.symbol) >= 0 ? '+' : ''}${calculate24hrChange(asset.symbol).toFixed(2)}%`
                      : asset.change_percentage || '+0.00%'
                    }
                    size="small"
                    sx={{
                      background: (assetType === 'crypto' 
                        ? calculate24hrChange(asset.symbol) >= 0
                        : parseFloat(asset.change_percentage || 0) >= 0
                      ) ? 'rgba(2, 192, 118, 0.2)' : 'rgba(248, 73, 96, 0.2)',
                      color: (assetType === 'crypto' 
                        ? calculate24hrChange(asset.symbol) >= 0
                        : parseFloat(asset.change_percentage || 0) >= 0
                      ) ? '#02C076' : '#F84960',
                      fontSize: '10px',
                      fontWeight: 600,
                      height: '18px'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                    {assetType === 'crypto' 
                      ? `Vol: ${formatNumber(asset.volumeUsd24Hr || 0)}` 
                      : 'Market'}
                  </Typography>
                </Box>
              </CardContent>
            </AssetTickerCard>
          ))}
        </AutoScrollAssetTicker>
        </AssetTickerContainer>
      </Box>

      <Grid container spacing={3}>
        
        {/* Assets & Market Performance */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Assets */}
            <Grid item xs={12} md={6}>
              <TradingCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '18px' }}>
                      {assetType === 'crypto' ? 'Crypto Assets' : 'Stock Holdings'}
                    </Typography>
                    <Box sx={{ display: 'flex', background: '#1E2328', borderRadius: '8px', padding: '3px' }}>
                      <Button
                        size="small"
                        onClick={() => setAssetType('stocks')}
                        sx={{
                          minWidth: '70px',
                          fontSize: '12px',
                          padding: '6px 12px',
                          background: assetType === 'stocks' ? '#F7931A' : 'transparent',
                          color: assetType === 'stocks' ? '#000' : '#B7BDC6',
                          borderRadius: '6px',
                          fontWeight: assetType === 'stocks' ? 700 : 500,
                          '&:hover': {
                            background: assetType === 'stocks' ? '#F7931A' : 'rgba(247, 147, 26, 0.1)'
                          }
                        }}
                      >
                        Stocks
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setAssetType('crypto')}
                        sx={{
                          minWidth: '70px',
                          fontSize: '12px',
                          padding: '6px 12px',
                          background: assetType === 'crypto' ? '#F7931A' : 'transparent',
                          color: assetType === 'crypto' ? '#000' : '#B7BDC6',
                          borderRadius: '6px',
                          fontWeight: assetType === 'crypto' ? 700 : 500,
                          '&:hover': {
                            background: assetType === 'crypto' ? '#F7931A' : 'rgba(247, 147, 26, 0.1)'
                          }
                        }}
                      >
                        Crypto
                      </Button>
                    </Box>
                  </Box>
                  <List sx={{ padding: 0 }}>
                    {portfolioAssets.map((asset, index) => (
                      <ListItem 
                        key={index} 
                        sx={{ 
                          padding: '12px 0', 
                          borderBottom: index < portfolioAssets.length - 1 ? '1px solid #2B3139' : 'none',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          mb: 1,
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            background: 'rgba(247, 147, 26, 0.08)',
                            transform: 'translateX(4px)'
                          }
                        }}
                        onClick={() => handleAssetClick(assetType === 'crypto' 
                          ? { symbol: asset.symbol } 
                          : { short_name: asset.symbol }, assetType)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar 
                                  src={asset.logo}
                                  sx={{ width: 32, height: 32, background: '#F7931A', fontSize: '12px' }}
                                >
                                  {asset.symbol[0]}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '16px' }}>
                                    {asset.symbol}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '12px' }}>
                                    {asset.name}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '16px' }}>
                                  {asset.priceUsd ? formatPrice(asset.priceUsd) : '$0.00'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: asset.change.startsWith('+') ? '#02C076' : asset.change.startsWith('-') ? '#F84960' : '#B7BDC6', fontSize: '12px', fontWeight: 600 }}>
                                  {asset.change}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '12px', display: 'block' }}>
                                  {assetType === 'crypto' ? `${asset.amount} (${asset.usdValue})` : asset.amount}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </TradingCard>
            </Grid>

            {/* Trading Bots */}
            <Grid item xs={12} md={6}>
              <TradingCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '18px' }}>
                      Trading Bots
                    </Typography>
                    <Button
                      component={RouterLink}
                      to="/package/Trading Bots"
                      size="small"
                      sx={{
                        fontSize: '12px',
                        padding: '6px 12px',
                        background: 'rgba(247, 147, 26, 0.1)',
                        color: '#F7931A',
                        borderRadius: '6px',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'rgba(247, 147, 26, 0.2)'
                        }
                      }}
                    >
                      View All
                    </Button>
                  </Box>
                  
                  {plans && plans.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {plans
                        .filter(plan => plan.category?.name === 'Trading Bots')
                        .slice(0, 3)
                        .map((plan, index) => (
                          <Box
                            key={plan.id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '12px',
                              background: '#1E2328',
                              borderRadius: '8px',
                              border: '1px solid #2B3139',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: '#F7931A',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 8px rgba(247, 147, 26, 0.2)'
                              }
                            }}
                            onClick={() => navigate(`/package/${plan.category?.name}`)}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                              <Box
                                sx={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.2) 0%, rgba(247, 147, 26, 0.1) 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid rgba(247, 147, 26, 0.3)'
                                }}
                              >
                                <SmartToyIcon sx={{ color: '#F7931A', fontSize: '20px' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '14px' }}>
                                  {plan.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '11px' }}>
                                  {formatPrice(plan.min_amount)} - {parseInt(plan.amount_invested) >= 1000000 ? 'UNLIMITED' : formatPrice(plan.amount_invested)}
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box sx={{ width: '60px', height: '40px' }}>
                                <SmallChart seed={plan.id} targetPercentage={parseFloat(plan.total_earning)} />
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ color: '#02C076', fontWeight: 700, fontSize: '14px' }}>
                                  {parseFloat(plan.total_earning).toFixed(2)}%
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#B7BDC6', fontSize: '10px' }}>
                                  ROI
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', padding: '32px 16px' }}>
                      <SmartToyIcon sx={{ color: '#6C7293', fontSize: '48px', mb: 2 }} />
                      <Typography variant="body2" sx={{ color: '#6C7293', mb: 1 }}>
                        No Trading Bots Available
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6C7293', fontSize: '11px' }}>
                        Check back later for new bot strategies
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </TradingCard>
            </Grid>
          </Grid>
        </Grid>

      </Grid>

      {/* Hidden components for backward compatibility */}
      <Box sx={{ display: 'none' }}>
        <CoinSlide />
        <BalanceCard />
        <StockSlide />
      </Box>

    </TradingDashboard>
  )
}

export default Index